import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useFundContext from '@/contexts/Fund/useFundContext';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { SupplierFullDtoIn } from '@/models/in/SupplierFullDtoIn';
import { ProjectDtoIn } from '@/models/in/ProjectDtoIn';
import { ProjectStatus } from '@/models/common/ProjectStatus';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import SupplierListItem from './SupplierListItem';
import { isSupplierOfferable, supplierApprovalFor } from './supplierProjectApproval';
import ProjectListItem from './ProjectListItem';
import styles from './ChooseSupplier.module.scss';

export interface SelectedSupplierData {
  id?: number;
  name: string;
  taxId: string;
  paymentInfos?: SupplierFullDtoIn['paymentInfos'];
}

/** The project a fund-expense request is filed against (HAT-1105), chosen before the supplier. */
export interface SelectedProjectData {
  id: number;
  name: string;
}

interface ChooseSupplierProps {
  onStart: (supplier: SelectedSupplierData, project?: SelectedProjectData | null) => void;
  /**
   * Passed only when the projects feature is on for the fund (HAT-1105, 22/10/2026). The tab then
   * asks for a project FIRST and a supplier second, as the office asked:
   *   "בבקשת תמיכה עבור הוצאות פרוייקט צריך להופיע כל הפרוייקטים והספקים המאושרים של הקרן, ההליך
   *    צריך להיות בחירת פרוייקט ולאחר מכן בחירת ספק; במידה ואין לו ספק או פרוייקט הוא יכול להקים
   *    אותם דרך שם".
   * Left undefined, the tab is the supplier-only chooser it always was - which is what every fund
   * without the feature keeps getting.
   */
  projectStep?: {
    /** "הקמת מיזם חדש" - the project is not there yet, so the coordinator goes and opens one. */
    onCreateProject: () => void;
  };
}

const ChooseSupplier = ({ onStart, projectStep }: ChooseSupplierProps) => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierFullDtoIn | null>(null);
  const [selectedProject, setSelectedProject] = useState<SelectedProjectData | null>(null);
  const [isNewSupplier, setIsNewSupplier] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [newSupplierTaxId, setNewSupplierTaxId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: suppliers, isLoading } = useGetAuthApi<SupplierFullDtoIn[]>(
    currentFund?.id ? `funds/${currentFund.id}/suppliers` : ''
  );

  // All of the fund's projects, not just the approved: only approved ones can be chosen (the server
  // enforces it too), but how many are still waiting is worth telling a coordinator who sees none.
  const { data: projects, isLoading: isLoadingProjects } = useGetAuthApi<ProjectDtoIn[]>(
    projectStep && currentFund?.id ? `funds/${currentFund.id}/projects` : ''
  );

  const approvedProjects = useMemo(
    () => (projects ?? []).filter((project) => project.status === ProjectStatus.Approved),
    [projects]
  );
  const waitingProjectsCount = (projects ?? []).filter(
    (project) => project.status !== ProjectStatus.Approved && project.status !== ProjectStatus.Rejected
  ).length;

  // SPR-10302: the office approves a supplier per project, so once a project is chosen it is the
  // supplier's approval for THAT project that counts.
  const chosenProjectId = projectStep ? selectedProject?.id : null;

  const filteredSuppliers = useMemo(() => {
    // A supplier the office rejected - outright or for this project - is not paid (the server refuses
    // it), so it is not offered. One still waiting IS offered, marked as such: "להגיש בקשת תשלום לספק
    // גם אם הוא לא אושר עדיין".
    const payable = (suppliers ?? []).filter((supplier) => isSupplierOfferable(supplier, chosenProjectId));
    if (!searchQuery.trim()) return payable;

    const query = searchQuery.toLowerCase();
    return payable.filter((supplier) =>
      (supplier.name ?? '').toLowerCase().includes(query) || (supplier.taxId ?? '').includes(query)
    );
  }, [suppliers, searchQuery, chosenProjectId]);

  const handleSupplierClick = (supplier: SupplierFullDtoIn) => {
    setSelectedSupplier(supplier);
    setIsNewSupplier(false);
  };

  const handleProjectClick = (project: SelectedProjectData) => {
    setSelectedProject(project);
    // A supplier picked for another project may be one the office rejected for this one.
    if (selectedSupplier && !isSupplierOfferable(selectedSupplier, project.id)) {
      setSelectedSupplier(null);
    }
  };

  const handleNewSupplierClick = () => {
    setSelectedSupplier(null);
    setIsNewSupplier(true);
  };

  const needsProject = !!projectStep;
  // With the project step on, the supplier part waits until a project has been chosen.
  const supplierStepOpen = !needsProject || selectedProject !== null;

  const hasSupplier = isNewSupplier
    ? newSupplierName.trim() !== '' && newSupplierTaxId.trim() !== ''
    : selectedSupplier !== null;
  const canStart = supplierStepOpen && hasSupplier;

  const handleStartClick = () => {
    if (!canStart) return;

    const project = needsProject ? selectedProject : null;
    if (isNewSupplier) {
      onStart({ name: newSupplierName.trim(), taxId: newSupplierTaxId.trim() }, project);
    } else if (selectedSupplier) {
      onStart(
        {
          id: selectedSupplier.id,
          name: selectedSupplier.name,
          taxId: selectedSupplier.taxId,
          paymentInfos: selectedSupplier.paymentInfos,
        },
        project
      );
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.contentArea}>
        {projectStep && (
          <section className={styles.stepSection} aria-labelledby="request-project-step">
            <div className={styles.stepHeader}>
              <h3 id="request-project-step" className="body-lr-bold text-(--font-heading)">
                {t('projects.supportRequest.projectStepTitle')}
              </h3>
              <button type="button" className={styles.stepAction} onClick={projectStep.onCreateProject}>
                + {t('projects.supportRequest.createProject')}
              </button>
            </div>

            {isLoadingProjects ? (
              <div className="p-4 text-center">{t('loading')}</div>
            ) : approvedProjects.length === 0 ? (
              <div className={styles.stepEmpty}>
                <p className="body-lr-regular text-(--font-body-1)">{t('projects.supportRequest.noApprovedProjects')}</p>
                <p className="body-sm-regular text-(--font-body-2)">
                  {waitingProjectsCount > 0
                    ? t('projects.supportRequest.waitingProjectsHint', { count: waitingProjectsCount })
                    : t('projects.supportRequest.noApprovedProjectsHint')}
                </p>
              </div>
            ) : (
              <div role="radiogroup" aria-labelledby="request-project-step">
                {approvedProjects.map((project) => (
                  <ProjectListItem
                    key={project.id}
                    name={project.name}
                    typeName={project.projectTypeName}
                    isSelected={selectedProject?.id === project.id}
                    onClick={() => handleProjectClick({ id: project.id, name: project.name })}
                  />
                ))}
                {waitingProjectsCount > 0 && (
                  <p className="body-sm-regular text-(--font-body-2) px-4 pt-2">
                    {t('projects.supportRequest.waitingProjectsHint', { count: waitingProjectsCount })}
                  </p>
                )}
              </div>
            )}
          </section>
        )}

        {needsProject && (
          <div className={styles.stepHeader} style={{ maxWidth: 674, width: '100%' }}>
            <h3 className="body-lr-bold text-(--font-heading)">{t('projects.supportRequest.supplierStepTitle')}</h3>
          </div>
        )}

        {!supplierStepOpen ? (
          <p className="body-sm-regular text-(--font-body-2) py-6">{t('projects.supportRequest.chooseProjectFirst')}</p>
        ) : (
          <>
            <div className={styles.searchContainer}>
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={t('suppliersManagement.search')}
              />
            </div>

            <div className={styles.listContainer}>
              {/* New supplier option */}
              <div
                className={`${styles.newSupplierOption} ${isNewSupplier ? styles.selected : ''}`}
                onClick={handleNewSupplierClick}
              >
                <span className="body-lr-bold text-(--font-body-1)">
                  + {t('newSupplier')}
                </span>
              </div>

              {/* New supplier form */}
              {isNewSupplier && (
                <div className={styles.newSupplierForm}>
                  <div className={styles.newSupplierFields}>
                    <div className="flex-1">
                      <label className="body-sm-regular text-(--font-heading) block mb-1.5">
                        {t('supplierName')}
                      </label>
                      <input
                        type="text"
                        className="body-md-regular text-(--font-body-1) p-3 border border-(--border-input-default) rounded-md w-full"
                        value={newSupplierName}
                        onChange={(e) => setNewSupplierName(e.target.value)}
                        placeholder={t('pleaseEnterName') || ''}
                        autoFocus
                      />
                    </div>
                    <div className="flex-1">
                      <label className="body-sm-regular text-(--font-heading) block mb-1.5">
                        {t('supplierIdentifier')}
                      </label>
                      <input
                        type="text"
                        className="body-md-regular text-(--font-body-1) p-3 border border-(--border-input-default) rounded-md w-full"
                        value={newSupplierTaxId}
                        onChange={(e) => setNewSupplierTaxId(e.target.value)}
                        placeholder={t('pleaseEnterCompanyNumber') || ''}
                      />
                    </div>
                  </div>
                  {needsProject && (
                    // The part the office asked to make possible: file the request now, approve the supplier later.
                    <p className="body-sm-regular text-(--font-body-2) mt-3">{t('projects.supportRequest.newSupplierHint')}</p>
                  )}
                </div>
              )}

              {/* Existing suppliers list */}
              {isLoading ? (
                <div className="p-6 text-center">טוען...</div>
              ) : filteredSuppliers.length === 0 && !isNewSupplier ? (
                <div className="p-6 text-center">
                  {searchQuery ? t('noSuppliersFound') : t('noSuppliersInFund')}
                </div>
              ) : (
                filteredSuppliers.map((supplier) => (
                  <SupplierListItem
                    key={supplier.id}
                    supplier={supplier}
                    isSelected={selectedSupplier?.id === supplier.id}
                    onClick={handleSupplierClick}
                    approvalStatus={supplierApprovalFor(supplier, chosenProjectId)}
                  />
                ))
              )}
            </div>
          </>
        )}
      </div>
      <footer className={styles.footer}>
        <button className={styles.startButton} onClick={handleStartClick} disabled={!canStart}>
          <span className={`heading-h4-semibold ${!canStart ? 'text-(--font-on-disabled)' : 'text-(--font-body-4)'}`}>
            {t('start')}
          </span>
        </button>
      </footer>
    </div>
  );
};

export default ChooseSupplier;
